import type { ReactNode } from "react";

import { Clock, FileText, Lock, Phone, Shield, Train } from "lucide-react";

import { AUTH_STRINGS as S } from "@/core/constants/auth-strings";
import { useNowFormatted } from "@/shared/hooks/useNowFormatted";

/**
 * Shared layout for the auth screens (RSM portal design): blue header,
 * centered card, yellow security notice, footer with copyright line.
 * Design tokens come from theme/tokens.css.
 */
export interface AuthLayoutProps {
  children: ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const headerDateTime = useNowFormatted();
  const year = new Date().getFullYear();

  return (
    <div className="flex min-h-screen flex-col" style={{ background: "var(--color-bg-app)" }}>
      {/* ═══ Header ═══ */}
      <header className="text-white" style={{ background: "var(--color-brand-primary)" }}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-5">
          <div className="flex items-center gap-3">
            <Train className="h-7 w-7 shrink-0" aria-hidden="true" />
            <div>
              <h1 className="text-xl font-bold leading-tight sm:text-[22px]">{S.workshopName}</h1>
              <p className="text-sm text-white/85">{S.railwayName}</p>
            </div>
          </div>
          <div
            className="hidden items-center gap-2 text-sm text-white/90 sm:flex"
            aria-hidden="true"
          >
            <Clock className="h-4 w-4" />
            <span>{headerDateTime}</span>
          </div>
        </div>
      </header>

      {/* ═══ Main content (centered card) ═══ */}
      <main className="flex flex-1 items-start justify-center px-4 py-8 sm:py-12">
        <div className="w-full" style={{ maxWidth: 480 }}>
          <div
            className="p-6 sm:p-8"
            style={{
              background: "var(--color-bg-card)",
              borderRadius: 8,
              boxShadow: "var(--shadow-card)",
            }}
          >
            {children}
          </div>
        </div>
      </main>

      {/* ═══ Security notice banner ═══ */}
      <section className="mb-6 px-4">
        <div
          className="mx-auto flex max-w-5xl gap-3 rounded-md px-4 py-3"
          style={{
            background: "var(--color-warn-bg)",
            borderLeft: "4px solid var(--color-warn-border)",
          }}
        >
          <div className="flex shrink-0 items-center gap-1 text-amber-700">
            <Shield className="h-4 w-4" aria-hidden="true" />
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          </div>
          <div className="text-sm">
            <p className="font-semibold text-amber-900">{S.securityNoticeTitle}</p>
            <p className="mt-0.5 text-amber-800">{S.securityNoticeBody}</p>
          </div>
        </div>
      </section>

      {/* ═══ Footer ═══ */}
      <footer className="border-t bg-white" style={{ borderColor: "var(--color-border-rsm)" }}>
        <div className="mx-auto max-w-7xl px-6 py-4">
          <div
            className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between"
            style={{ color: "var(--color-text-secondary)" }}
          >
            <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label="Footer links">
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                {S.footerTerms}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                {S.footerPrivacy}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                {S.footerContact}
              </span>
            </nav>
            <p className="inline-flex items-center gap-1.5">
              <Train className="h-3.5 w-3.5" aria-hidden="true" />
              <span>
                {S.railwayName}
                <span className="mx-2 text-slate-400">|</span>
                {S.ministryAttribution}
              </span>
            </p>
          </div>
          <p className="mt-3 text-center text-xs" style={{ color: "var(--color-text-muted)" }}>
            {S.copyrightLine(year)}
          </p>
        </div>
      </footer>
    </div>
  );
}

/**
 * The round WMS monogram seal used at the top of each auth card —
 * same construction as the RSM seal with WMS branding.
 */
export function WmsSeal() {
  return (
    <div className="flex justify-center">
      <div
        aria-hidden="true"
        className="flex h-24 w-24 items-center justify-center rounded-full border-2 bg-white"
        style={{ borderColor: "var(--color-text-primary)" }}
      >
        <div
          className="flex h-[84px] w-[84px] flex-col items-center justify-center rounded-full border"
          style={{
            borderColor: "var(--color-border-rsm)",
            color: "var(--color-text-primary)",
          }}
        >
          <span className="text-[9px] font-semibold tracking-[0.2em]">E.R.</span>
          <div className="mt-1 flex items-center gap-1">
            <Train className="h-4 w-4" />
            <span className="font-serif text-lg font-bold leading-none">WMS</span>
          </div>
          <span className="mt-1 text-[7px] font-medium tracking-[0.18em]">WHEEL MGMT</span>
        </div>
      </div>
    </div>
  );
}
